import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserService from '../../services/userService';
import { useSupabaseAuthClient, useSupabaseUser } from '../../hooks/useSupabaseAuthClient';
import { useTranslation } from '../../i18n';
import logo from '../../styles/img/Logo.png';
import './NavigationBar.css';

const NavigationBar = () => {
  const { state, dispatch } = useAuth();
  const { t, lang, setLang } = useTranslation();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const userMenuRef = useRef(null);
  const toolsDropdownRef = useRef(null);
  const client = useSupabaseAuthClient();
  const user = useSupabaseUser();
  
  // Close user dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (toolsDropdownRef.current && !toolsDropdownRef.current.contains(event.target)) {
        setToolsDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // Handle scrolling effects
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setToolsDropdownOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [mobileMenuOpen]);
  
  const handleLogout = async (e) => {
    e.preventDefault();
    console.log('NavigationBar: Logout button clicked');
    try {
      console.log('NavigationBar: Calling UserService.logoutWithClient...');
      await UserService.logoutWithClient(client, dispatch, user);
      console.log('NavigationBar: Logout successful');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };
  
  const toggleToolsDropdown = (e) => {
    e.preventDefault();
    setToolsDropdownOpen(!toolsDropdownOpen);
  };
  
  return (
    <header className={`navbar-wrapper ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container container">
        <div className="navbar-brand">
          <Link to="/dashboard" className="navbar-logo">
            <img src={logo} alt="SecurePaper" className="logo-image" />
            <span className="logo-text">Secure<span className="text-primary">Paper</span></span>
          </Link>
        </div>
        
        <button 
          className={`mobile-toggle ${mobileMenuOpen ? 'active' : ''}`} 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('nav.toggleMenu')}
          aria-expanded={mobileMenuOpen}
          aria-controls="navbar-menu"
        >
          <span className="toggle-bar"></span>
          <span className="toggle-bar"></span>
          <span className="toggle-bar"></span>
        </button>

        <nav id="navbar-menu" className={`navbar-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="navbar-start">
            
            <NavLink 
              to="/create_encrypted_text" 
              className={({isActive}) => `navbar-item highlight ${isActive ? 'active' : ''}`}
            >
              <i className="fa fa-lock" aria-hidden="true"></i> {t('nav.createSecurePaper')}
            </NavLink>
            
            <NavLink 
              to="/decrypt" 
              className={({isActive}) => `navbar-item scan-highlight ${isActive ? 'active' : ''}`}
            >
              <i className="fa fa-qrcode" aria-hidden="true"></i> {t('nav.decryptSecurePaper')}
            </NavLink>

            <a href="https://securepaper.app" className="navbar-item">
              {t('nav.website')}
            </a>
          </div>
          
          <div className="navbar-end">
            <div className="lang-switcher" role="group" aria-label={t('nav.language')}>
              <button
                type="button"
                className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => setLang('en')}
                aria-pressed={lang === 'en'}
              >
                EN
              </button>
              <span className="lang-divider">/</span>
              <button
                type="button"
                className={`lang-btn ${lang === 'pl' ? 'active' : ''}`}
                onClick={() => setLang('pl')}
                aria-pressed={lang === 'pl'}
              >
                PL
              </button>
            </div>
            {state.isLoggedIn ? (
              <div className="user-menu" ref={userMenuRef}>
                <button 
                  className="user-menu-button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  aria-label={t('nav.userMenu')}
                  aria-expanded={userMenuOpen}
                >
                  <span className="user-icon">
                    {state.user?.email?.charAt(0).toUpperCase() || <i className="fa fa-user" aria-hidden="true"></i>}
                  </span>
                  <span className="user-email d-none d-md-inline">{state.user?.email || t('nav.user')}</span>
                  <i className={`fa fa-chevron-${userMenuOpen ? 'up' : 'down'} dropdown-icon`} aria-hidden="true"></i>
                </button>
                
                {userMenuOpen && (
                  <div className="user-dropdown">
                    <div className="dropdown-header">
                      <span className="dropdown-user" style={{ 
                        maxWidth: '200px', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block'
                      }}>
                        {state.user?.email || t('nav.user')}
                      </span>
                    </div>
                    <div className="dropdown-divider"></div>
                    <NavLink to="/messages" className="dropdown-item">
                      <i className="fa fa-file-lines" aria-hidden="true"></i> {t('nav.history')}
                    </NavLink>
                    <NavLink to="/keys" className="dropdown-item">
                      <i className="fa fa-key" aria-hidden="true"></i> {t('nav.keys')}
                    </NavLink>
                    <button type="button" onClick={handleLogout} className="dropdown-item">
                      <i className="fa fa-arrow-right-from-bracket" aria-hidden="true"></i> {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="navbar-item auth-link login-link">
                  {t('nav.login')}
                </Link>
                <Link to="/signup" className="navbar-item auth-link signup-link">
                  {t('nav.signup')}
                </Link>
              </>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default NavigationBar;
